import * as React from "react";
import { Eye, EyeOff } from "lucide-react";

import { cn } from "@/lib/utils";

export type PasswordInputProps = Omit<React.ComponentProps<"input">, "type">;

/**
 * Password field with a show/hide toggle.
 *
 * Browsers disagree about what they put inside a password field on their own —
 * Edge renders its own reveal eye, Chrome and Safari render a password-manager
 * button, Firefox renders nothing — so the toggle is ours and Edge's duplicate
 * is suppressed in `index.css` (`::-ms-reveal`). Password-manager buttons are
 * deliberately left alone.
 *
 * Forwards its ref, so `{...register("password")}` works unchanged.
 */
const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, id, ...props }, ref) => {
    const [isVisible, setIsVisible] = React.useState(false);

    return (
      <div className="relative">
        <input
          {...props}
          id={id}
          ref={ref}
          type={isVisible ? "text" : "password"}
          // Padding comes after `className` so the caller's own `px-*` doesn't
          // let long values run underneath the toggle.
          className={cn(className, "pr-11")}
        />
        <button
          type="button"
          onClick={() => {
            setIsVisible((visible) => !visible);
          }}
          aria-label={isVisible ? "Hide password" : "Show password"}
          aria-pressed={isVisible}
          aria-controls={id}
          title={isVisible ? "Hide password" : "Show password"}
          className="absolute top-1/2 right-3 flex -translate-y-1/2 cursor-pointer items-center justify-center text-[#8d8d8d] transition-colors hover:text-[#373737] focus-visible:rounded-xs focus-visible:text-[#373737] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#23517F]"
        >
          {isVisible ? (
            <EyeOff className="h-[18px] w-[18px]" aria-hidden="true" />
          ) : (
            <Eye className="h-[18px] w-[18px]" aria-hidden="true" />
          )}
        </button>
      </div>
    );
  }
);
PasswordInput.displayName = "PasswordInput";

export { PasswordInput };
